package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.PlanStatus
import dev.lingvo.shared.Role
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCreditCardIcon
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.state.WorkspaceSelection

/**
 * Says that the card failed, on every screen, until somebody fixes it.
 *
 * **`/app/settings/billing/fix-payment` existed and nothing in the product linked to it.** The page
 * had been built, and the only way to reach it was to type the URL — so a workspace whose card had
 * died could carry on for a month with no signal outside the billing page, which is the one page
 * nobody visits when nothing has gone wrong. A route with no inbound link is a route that does not
 * exist.
 *
 * **Not dismissible, and that is a deliberate exception.** Every other notice in this shell can be
 * ignored into the background. This one reports a subscription that is actively ending: dismissing
 * it would hide a deadline the reader did not set and cannot see anywhere else. It is also the
 * highest-priority notice, so it is the one the stack keeps visible when several apply at once.
 *
 * **Role-split copy**, because the two audiences can do different things. Only the owner can replace
 * the card — `GET /billing/portal` is owner-only, and both billing pages say so — so only the owner
 * gets the action. Everybody else, admins included, would be following an instruction they cannot
 * carry out: they get the fact and the name of the one person who can act on it.
 *
 * **The split used to sit at `ADMIN`**, on the stated belief that "an admin can replace the card".
 * The billing pages later contradicted it in three places, so an admin followed this banner's button
 * to `/fix-payment` and read "Only the owner can update the card" with no button under it, while a
 * translator was sent to ask an admin who could not help — both while the dunning clock ran.
 *
 * Reads `planStatus` off the *rendered* workspace rather than the session, so a person who belongs
 * to a healthy workspace and a failing one is warned about the one they are looking at.
 *
 * Returns the notice rather than drawing it: [ShellNotices] caps the stack at one visible bar, and
 * a banner that painted itself could not be ranked against the others.
 */
@Composable
internal fun pastDueNotice(): ShellNotice? {
    val workspace = WorkspaceSelection.current() ?: return null
    if (workspace.planStatus != PlanStatus.PAST_DUE) return null

    val ctx = rememberPageContext()
    val canFix = workspace.role == Role.OWNER

    return ShellNotice(
        id = "past-due",
        // `{workspace}` rather than a Kotlin template: a string template is a sentence no catalogue
        // can hold, and this one is on screen for every member of a workspace whose card has died —
        // including the translators who cannot fix it and most need to understand it.
        title = tf(
            "past_due_banner.a_payment_for_workspace_failed",
            "A payment for {workspace} failed.",
            "workspace" to workspace.name,
        ),
        description = if (canFix) {
            t(
                "past_due_banner.update_the_card_to_keep_the",
                "Update the card to keep the plan.",
            )
        } else {
            t(
                "past_due_banner.the_workspace_owner_needs_to_update",
                "The workspace owner needs to update the card.",
            )
        },
        variant = LAlertVariant.DANGER,
        action = if (canFix) {
            {
                LButton(
                    label = t("past_due_banner.update_payment", "Update payment"),
                    onClick = { ctx.router.navigateTo("/app/settings/billing/fix-payment") },
                    variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM,
                    leadingIcon = { LCreditCardIcon() },
                )
            }
        } else {
            null
        },
    )
}
